import { supabase } from '@/integrations/supabase/client';
import { DocumentRow } from '@/types';

// The `documents` table is brand-new (migration 20260513160000_storage.sql) and
// hasn't been picked up by the generated Supabase types yet. We use a local
// shape that mirrors the SQL schema; once Sergio regenerates types via
// `supabase gen types typescript` the `from('documents' as never)` casts can
// be removed in favor of the typed table name.
interface DocumentRowDb {
  id: string;
  workspace_id: string;
  evento_id: string | null;
  proveedor_id: string | null;
  storage_path: string;
  nombre: string;
  tipo: string | null;
  size_bytes: number | null;
  mime_type: string | null;
  created_at: string;
}

const DOCUMENTS_BUCKET = 'workspace-documents';
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 60 minutes

const documentRowFromDb = (row: DocumentRowDb): DocumentRow => ({
  id: row.id,
  workspace_id: row.workspace_id,
  evento_id: row.evento_id,
  proveedor_id: row.proveedor_id,
  storage_path: row.storage_path,
  nombre: row.nombre,
  tipo: row.tipo,
  size_bytes: row.size_bytes,
  mime_type: row.mime_type,
  created_at: new Date(row.created_at),
});

// Workspace_id is derived from the current session — never trust a
// client-supplied value for writes. RLS will reject anything else anyway, but
// short-circuiting here gives a friendlier error and avoids orphaned uploads.
const getCurrentWorkspaceId = async (): Promise<string> => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session?.user?.id) {
    throw new Error('Usuario no autenticado');
  }
  return session.user.id; // workspace_id == auth.uid() in CoPlanner schema
};

const fileExtension = (filename: string): string => {
  const idx = filename.lastIndexOf('.');
  if (idx < 0 || idx === filename.length - 1) return '';
  return filename.slice(idx + 1).toLowerCase();
};

// Lightweight RFC-4122-ish v4 UUID. crypto.randomUUID() is fine in modern
// browsers and Node/Vitest; fall back to a manual generator just in case.
const newUuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Non-cryptographic fallback — only used if randomUUID is missing.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export interface UploadDocumentParams {
  file: File;
  eventoId?: string;
  proveedorId?: string;
  tipo?: string;
  nombre?: string;
}

export interface ListDocumentsParams {
  eventoId?: string;
  proveedorId?: string;
}

export interface SignedUrl {
  url: string;
  expiresAt: Date;
}

export const storageService = {
  uploadDocument: async ({
    file,
    eventoId,
    proveedorId,
    tipo,
    nombre,
  }: UploadDocumentParams): Promise<DocumentRow> => {
    const workspaceId = await getCurrentWorkspaceId();
    const ext = fileExtension(file.name);
    const storagePath = ext
      ? `${workspaceId}/${newUuid()}.${ext}`
      : `${workspaceId}/${newUuid()}`;

    // 1. Upload the blob first.
    const { error: uploadError } = await supabase
      .storage
      .from(DOCUMENTS_BUCKET)
      .upload(storagePath, file, {
        contentType: file.type || undefined,
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }

    // 2. Insert the metadata row. If this fails, clean up the orphan blob.
    const insertPayload = {
      workspace_id: workspaceId,
      evento_id: eventoId ?? null,
      proveedor_id: proveedorId ?? null,
      storage_path: storagePath,
      nombre: nombre || file.name,
      tipo: tipo ?? null,
      size_bytes: file.size,
      mime_type: file.type || null,
    };

    const { data, error: insertError } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('documents' as any)
      .insert(insertPayload)
      .select()
      .single();

    if (insertError || !data) {
      // Best-effort cleanup — swallow any remove error so the original insert
      // error propagates to the caller.
      await supabase.storage.from(DOCUMENTS_BUCKET).remove([storagePath]).catch(() => undefined);
      throw insertError ?? new Error('Error al guardar el documento');
    }

    return documentRowFromDb(data as unknown as DocumentRowDb);
  },

  listDocuments: async ({ eventoId, proveedorId }: ListDocumentsParams = {}): Promise<DocumentRow[]> => {
    let query = supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('documents' as any)
      .select('*')
      .order('created_at', { ascending: false });

    if (eventoId) {
      query = query.eq('evento_id', eventoId);
    }
    if (proveedorId) {
      query = query.eq('proveedor_id', proveedorId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return ((data ?? []) as unknown as DocumentRowDb[]).map(documentRowFromDb);
  },

  getSignedUrl: async (documentId: string): Promise<SignedUrl> => {
    const { data: doc, error: lookupError } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('documents' as any)
      .select('storage_path')
      .eq('id', documentId)
      .single();

    if (lookupError || !doc) {
      throw lookupError ?? new Error('Documento no encontrado');
    }

    const storagePath = (doc as unknown as { storage_path: string }).storage_path;

    const { data: signed, error: signError } = await supabase
      .storage
      .from(DOCUMENTS_BUCKET)
      .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);

    if (signError || !signed?.signedUrl) {
      throw signError ?? new Error('No se pudo generar la URL del documento');
    }

    return {
      url: signed.signedUrl,
      expiresAt: new Date(Date.now() + SIGNED_URL_TTL_SECONDS * 1000),
    };
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    const { data: doc, error: lookupError } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('documents' as any)
      .select('storage_path')
      .eq('id', documentId)
      .single();

    if (lookupError || !doc) {
      throw lookupError ?? new Error('Documento no encontrado');
    }

    const storagePath = (doc as unknown as { storage_path: string }).storage_path;

    // Order matters: blob first, then row. If the blob delete fails, leave the
    // row in place so the user can retry — never end up with a metadata row
    // pointing at a missing object.
    const { error: removeError } = await supabase
      .storage
      .from(DOCUMENTS_BUCKET)
      .remove([storagePath]);

    if (removeError) {
      throw removeError;
    }

    const { error: deleteRowError } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('documents' as any)
      .delete()
      .eq('id', documentId);

    if (deleteRowError) {
      throw deleteRowError;
    }
  },
};

export const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
